// @TODO: not working on web (selected verses always Genesis 1:1-31 despite title being correct?)
/**
 * Ryan O'Dowd
 * 2022-04-05
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  BOOK_SORT_METHODS,
  setBookSortMethod,
} from '@utils/store/actions';
import {
  FlatList,
  TouchableOpacity,
  View,
} from 'react-native';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import BookProgress from '@components/BookProgress';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import Dialog from '@components/common/Dialog';
import EmptyScreen from '@components/common/EmptyScreen';
import Icon from '@react-native-vector-icons/material-design-icons';
import {
  IconButton,
} from 'react-native-paper';
import ListItem from '@components/ListItem';
import Menu from '@components/common/Menu';
import Searchbar from '@components/common/Searchbar';
import Text from '@components/common/Text';
import TranslationSelectionDialog from '@components/TranslationSelectionDialog';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const PassageSelection = (props: PassageSelectionProps) => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const bookListRef = useRef(undefined);

  hooks.useBackButton(() => {
    return false;
  });

  const [_listItemHeight, setListItemHeight] = useState(0);
  const [_initialScrollComplete, setInitialScrollComplete] = useState(!props.selectedChapter?.book_id);

  /* @TODO: useLayoutEffect is preferred to onLayout as of rn 0.76.0
  const listItemRef = useRef(null);

  useLayoutEffect(() => {
    listItemRef.current?.measureInWindow((x, y, width, height) => {
      if (_listItemHeight === 0) {
        setListItemHeight(height);
      }
    });
  }, []);
  */

  const _scrollToBook = useCallback((bookId) => {
    if (_listItemHeight === 0) {
      return;
    }

    bookListRef.current?.scrollToOffset({
      offset: (_listItemHeight + (styles.flatList.gap ?? 0)) * (bookId - 1),
      animated: true,
    });
  }, [_listItemHeight, styles.flatList.gap]);

  const [_searchOpen, setSearchOpen] = useState(false);
  const [_searchText, setSearchText] = useState('');
  const [_isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [_isTranslationMenuOpen, setIsTranslationMenuOpen] = useState(false);

  const preferredTranslation = hooks.usePreferredTranslation();
  const wrinkles = hooks.useTypedSelector((state) => state.wrinkles);

  const bookSortMethod = hooks.useTypedSelector((state) => state.settingsBookSortMethod);

  const books = hooks.useTypedSelector((state) => state.books);
  const [_selectedBookId, setSelectedBookId] = useState(props.selectedChapter?.book_id);
  const [_selectedChapterNumber, setSelectedChapterNumber] = useState(null);

  const [_selectedVerseNums, setSelectedVerseNums] = useState([]);

  const sortedBooks = books.filter((book) => {
    const searchText = _searchText ? _searchText.toLocaleLowerCase() : ''; // @TODO: https://githubmate.com/repo/facebook/hermes/issues/582
    const searchTerms = searchText ? searchText.split(' ') : [];

    return !searchTerms.length || searchTerms.every((searchTerm) => {
      return CPassage.getBookName(preferredTranslation.id, book.id).toLocaleLowerCase().includes(searchTerm); // @TODO: generalize this so that search works for any translation (e.g. search for 'Song of Solomon' in NIV still shows 'Song of Songs')
    });
  }).sort((a, b) => {
    if (bookSortMethod === 'alphabetically') {
      return a.name.replace(/\d /g, '').localeCompare(b.name.replace(/\d /g, ''), {sensitivity: 'base'});
    }

    // they're already in order
    return a.id - b.id;
  });

  useEffect(() => {
    if (_initialScrollComplete && _selectedBookId) {
      _scrollToBook(_selectedBookId);
    }
  }, [_selectedBookId, _selectedChapterNumber, _scrollToBook, _listItemHeight, _initialScrollComplete]);

  const minSelectedVerseNum = _selectedVerseNums.length ? Math.min(..._selectedVerseNums) : undefined;
  const maxSelectedVerseNum = _selectedVerseNums.length ? Math.max(..._selectedVerseNums) : undefined;

  const validPassageSelected = _selectedBookId && _selectedChapterNumber && _selectedVerseNums.length;
  const passageSelectionStarted = _selectedBookId || _selectedChapterNumber || _selectedVerseNums.length;

  const _renderBook = (book) => { // @TODO: this needs to be much more performant; and probably be its own component
    return (
      <ListItem
        containerStyles={styles.listItemContainer}
        onLayout={(event) => {
          if (_listItemHeight === 0) {
            setListItemHeight(event.nativeEvent.layout.height);
          }
        }}
        onPress={() => {
          setSelectedBookId((curr) => curr === book.id ? null : book.id);
          setSelectedChapterNumber(null);
          setSelectedVerseNums([]);
        }}
        variant={utilities.isBookWrinkled(wrinkles, book) ? 'accent' : 'primary'}
        isExpanded={_listItemHeight > 0 && book.id === _selectedBookId}
        accordionContent={book.id !== _selectedBookId ? null // @TODO: <Text>Loading...</Text>// @TODO: prettier loading indicator...maybe skeleton
          : (
            <View
              style={styles.accordionInner}
              onLayout={() => {
                if (!_initialScrollComplete) {
                  setTimeout(() => { // @TODO: without this timeout, the scrolling doesn't work precisely when the selected book is towards the end of the list on initial load. however, without it, the book is still in view (at least on the simulator), so i feel comfortable with this for now
                    _scrollToBook(_selectedBookId);
                  }, 200);
                  setInitialScrollComplete(true);
                }
              }}
            >
              {_selectedChapterNumber &&
                <View style={styles.chapterActions}>
                  <TouchableOpacity
                    style={styles.backToChaptersWrapper}
                    onPress={() => {
                      setSelectedChapterNumber(null);
                      setSelectedVerseNums([]);
                    }}
                  >
                    <Icon
                      style={styles.backIcon}
                      name='chevron-left'
                    />
                    <Text style={styles.backText}>{i18n('allChapters')}</Text>
                  </TouchableOpacity>
                </View>
              }
              {/* @TODO: option to select all verses in chapter
  // @TODO: this signature has changed since this comment was added: const [_selectedVerseNums, onVersePress, clearVerseSelection, selectAllVerses] = hooks.useSelectVerses(props.selectedVerseNums);
  const allVerses = hooks.useGetVersesForPassages(props.passages); // @TODO: this doesn't get recalculated when Bible is deleted locally (edge case, but something to consider)
  const passagesVerses = [];
  props.passages.forEach((p) => {
    passagesVerses.push(...(allVerses[p.id] || []));
  });
  <FormControlLabel
    control={
      <Checkbox
        checked={props.allVersesSelected}
        onChange={(e) => {
          if (e.target.checked) {
            selectAllVerses(props.passages[0].end_verse_number);
          } else {
            clearVerseSelection();
          }
        }}
      />
    }
    label='Select all verses'
    // @TODO: tooltip helper
  />
              */}

              <BookProgress
                book={book}
                selectedChapterNumber={_selectedChapterNumber}
                selectedVerseNums={_selectedVerseNums}
                onChapterNumberSelect={setSelectedChapterNumber}
                setSelectedVerseNums={setSelectedVerseNums}
                chapterSelectionOnly={props.chapterSelectionOnly}
                onSelectPassage={props.onSelectPassage}
              />
            </View>
          )
        }
      >
        <View style={styles.rowWrapper}>
          <Text style={styles.bookName}>{CPassage.getBookName(preferredTranslation.id, book.id)}</Text>
          <View style={styles.rightData}>
            <Text style={styles.bookMetadata}>{Object.keys(book.chapters_and_verses).length} {Object.keys(book.chapters_and_verses).length === 1 ? i18n('chapter') : i18n('chapters')}</Text>
            <Icon
              style={styles.TODO}
              color={styles.colors.white}// @TODO: this could be in styles for more dynamic colors (e.g. 'colors.iconColor')?
              name={book.id === _selectedBookId ? 'chevron-up' : 'chevron-down'}
              size={20}
            />
          </View>
        </View>
      </ListItem>
    );
  };

  return (
    <View style={styles.container}>
      <Dialog
        visible={true}
        onDismiss={() => props.onClose()}
        fullWidth={true}
        // @TODO: props for closer to full screen
      >
        <Dialog.Title>
          <View style={styles.titleWrapper}>{/* @TODO: why can't this be part of Dialog.Title styles? */}
            <Text style={styles.titleText}>{props.chapterSelectionOnly ? 'Select chapter' : `${books.find((b) => b.id === _selectedBookId)?.name || '_____'} ${_selectedChapterNumber || '__'}:${minSelectedVerseNum || '__'}-${maxSelectedVerseNum || '__'}`}</Text>{/* @TODO: better empty text...also if only one verse, don't show end, e.g. Exodus 3:1-1 should be Exodus 3:1 */}{/* @TODO: should account for ranges (just use prettyPassageName instead...just have to worry about blanks) */}
            <View style={styles.rightActions}>
              <IconButton
                icon='magnify'
                iconColor={styles.colors.dialogText}
                onPress={() => {
                  setSearchText('');
                  setSearchOpen((curr) => !curr);
                }}
              />
              <Menu
                anchor={
                  <IconButton
                    // @TODO: dynamic color based on dark mode
                    icon='sort'
                    iconColor={styles.colors.dialogText}
                    onPress={() => setIsSortMenuOpen(true)}
                  />
                }
                visible={_isSortMenuOpen}
                onDismiss={() => setIsSortMenuOpen(false)}
              >
                {BOOK_SORT_METHODS.map((sortMethod) => {
                  const displayName = utilities.camelCaseToHumanReadable(sortMethod);
                  return (
                    <Menu.Item
                      key={sortMethod}
                      title={displayName}
                      titleStyle={bookSortMethod === sortMethod && styles.selectedMenuItem}// @TODO: convert this to a `selected` prop in a custom (wrapper) component for menu items
                      onPress={() => {
                        dispatch(setBookSortMethod(sortMethod));
                        setIsSortMenuOpen(false);
                      }}
                    />
                  );
                })}
              </Menu>
            </View>
          </View>
        </Dialog.Title>
        <Dialog.Content style={styles.innerContainer}>
          {_searchOpen &&
            <Searchbar
              onChangeText={(searchText) => setSearchText(searchText)}
              value={_searchText}
              placeholder={i18n('searchBooks')}
              // @TODO: this should allow a full reference to be passed in which autoselects the specified verses for copy/paste (especially helpful on web)
            />
          }
          {sortedBooks.length
            ? (
              <FlatList// @TODO: no height on native
                ref={bookListRef}
                style={styles.flatList}
                contentContainerStyle={styles.flatList}
                data={sortedBooks}
                renderItem={({item}) => _renderBook(item)}
              />
            ) : (
              <EmptyScreen.Wrapper avoidKeyboard={true}>
                <EmptyScreen.Text extraStyle={styles.emptyText}>
                  {i18n('noMatches', _searchText)}
                </EmptyScreen.Text>
              </EmptyScreen.Wrapper>
            )
          }
        </Dialog.Content>
        <Dialog.Actions>
          {!props.chapterSelectionOnly && // @TODO: don't couple with this prop
            <Button
              mode='outlined'
              onPress={() => setIsTranslationMenuOpen(true)}
            >
              {preferredTranslation.abbreviation}
            </Button>
          }

          <View style={styles.spacer} />

          <Button
            mode={props.chapterSelectionOnly ? 'contained' : 'text'}
            onPress={() => props.onClose()}
          >
            {i18n('cancel')}
          </Button>
          {!props.chapterSelectionOnly &&
            <Button
              disabled={!validPassageSelected}
              onPress={async () => {
                if (props.chapterSelectionOnly && passageSelectionStarted && !validPassageSelected) { // @TODO: test this...if passage selection was open, this was undefined?
                  props.onClose();
                } else {
                  if (validPassageSelected) {
                    const passage = await utilities.createNewPassage( // @TODO: should return CPassage
                      preferredTranslation.id,
                      _selectedBookId,
                      _selectedChapterNumber,
                      minSelectedVerseNum,
                      maxSelectedVerseNum,
                    );

                    props.onSelectPassage(passage);
                  }
                  props.onClose();
                }
              }}
            >
              {i18n('save')}
            </Button>
          }
        </Dialog.Actions>
      </Dialog>
      <TranslationSelectionDialog
        isOpen={_isTranslationMenuOpen}
        onClose={() => setIsTranslationMenuOpen(false)}
        autoClose={true}
        closeButtonText={i18n('close')}
      />
    </View>
  );
};

class PassageSelectionProps {
  onClose: () => void;
  onSelectPassage: (passageJson) => void; // @TODO: should be CPassage, not json

  selectedChapter?: any;
  chapterSelectionOnly?: boolean;
}

export default PassageSelection;
